/**
 * 与浏览器扩展通信 - 备份/还原书签
 */

const SOURCE = 'extbookmark';

export type ChromeBookmarkNode = {
  id: string;
  parentId?: string;
  index?: number;
  url?: string;
  title?: string;
  children?: ChromeBookmarkNode[];
};

export type RestoreFolder = {
  id: string;
  name: string;
  parent_id: string | null;
  position: number;
};

export type RestoreBookmark = {
  id: string;
  folder_id: string;
  name: string;
  url: string;
  note?: string;
  position: number;
};

function generateId(): string {
  return crypto.randomUUID();
}

function sendToExtension<T>(type: string, payload?: unknown): Promise<T> {
  return new Promise((resolve, reject) => {
    const requestId = generateId();
    const handler = (e: MessageEvent) => {
      if (e.data?.source !== SOURCE || e.data?.requestId !== requestId) return;
      window.removeEventListener('message', handler);
      if (e.data?.error) {
        reject(new Error(e.data.error));
      } else {
        resolve(e.data as T);
      }
    };
    window.addEventListener('message', handler);
    window.postMessage({ source: SOURCE, type, payload, requestId }, '*');
    setTimeout(() => {
      window.removeEventListener('message', handler);
      reject(new Error('扩展响应超时，请确保已安装并启用 extBookMark 扩展'));
    }, 15000);
  });
}

/** 请求扩展读取浏览器书签树 */
export async function requestBackupFromExtension(): Promise<ChromeBookmarkNode[]> {
  const res = await sendToExtension<{ tree: ChromeBookmarkNode[]; error?: string }>('EXBOOKMARK_BACKUP_REQUEST');
  if (res.error) throw new Error(res.error);
  return res.tree ?? [];
}

/** 请求扩展还原书签到浏览器 */
export async function requestRestoreToExtension(payload: {
  folders: RestoreFolder[];
  bookmarks: RestoreBookmark[];
}): Promise<void> {
  const res = await sendToExtension<{ success: boolean; error?: string }>('EXBOOKMARK_RESTORE_REQUEST', payload);
  if (!res.success || res.error) throw new Error(res.error ?? '还原失败');
}

/** 检测扩展是否可用（通过发送 ping 或检查响应） */
export function isExtensionAvailable(): Promise<boolean> {
  return requestBackupFromExtension()
    .then(() => true)
    .catch(() => false);
}
